import { Component, h } from 'preact'
import Header from '../header'
import AlertComponent from '../ui-components/AlertComponent'
import AccordionPanel from '../panel/AccordionPanel'
import { CloseInactiveTopicsPanelBody, CloseInactiveTopicsPanelHeader } from './CloseInactiveTopicsPanel'
import axios from 'axios'
import Loader from '../ui-components/Loader'
import { NotifyToMarkAnswerPanelBody, NotifyToMarkAnswerPanelHeader } from './NotifyToMarkAnswerPanel'
import ButtonControl from '../button-control'
import Modal from '../../components/reactModal'

interface AutomationRulesProps {
    closeInactiveTopicsEnabled: boolean,
    selectedCategoryList: Array<string>,
    closeTopicsAfter?: number,
    notifyMarkAnswerEnabled: boolean,
    notifyAfter?: number
}
interface Props extends AutomationRulesProps{
    categories: any[]
}

interface State {
    storedState: AutomationRulesProps,
    isCloseInactiveTopicsEnabled: boolean,
    selectedCategories: Array<string>,
    closeTopicsIn?: number,
    showCloseInactiveTopicsActionButtons: boolean,
    showCategoryDropdown: boolean,
    isLoading: boolean,
    showValidationError: boolean,

    isNotifyMarkAnswerEnabled: boolean,
    notifyAfter?: number,
    showNotifyMarkAnswerActionButtons: boolean,
    showNotifyMarkAnswerValidationError: boolean,

    hasUnsavedChanges: boolean,
    showUnsavedChangesModal: boolean,

    messageType?: string|null,
    message?: string,
    activePanel?: number|undefined
}

class AutomationRules extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      storedState: {
        closeInactiveTopicsEnabled: this.props.closeInactiveTopicsEnabled,
        selectedCategoryList: this.props.selectedCategoryList,
        closeTopicsAfter: this.props.closeTopicsAfter === 0 ? undefined : this.props.closeTopicsAfter,
        notifyMarkAnswerEnabled: this.props.notifyMarkAnswerEnabled,
        notifyAfter: this.props.notifyAfter === 0 ? undefined : this.props.notifyAfter
      },
      isCloseInactiveTopicsEnabled: this.props.closeInactiveTopicsEnabled,
      closeTopicsIn: this.props.closeTopicsAfter === 0 ? undefined : this.props.closeTopicsAfter,
      selectedCategories: this.props.selectedCategoryList,
      showCloseInactiveTopicsActionButtons: false,
      showCategoryDropdown: false,
      showValidationError: false,
      hasUnsavedChanges: false,
      showUnsavedChangesModal: false,
      isLoading: false,
      isNotifyMarkAnswerEnabled: this.props.notifyMarkAnswerEnabled,
      notifyAfter: this.props.notifyAfter === 0 ? undefined : this.props.notifyAfter,
      showNotifyMarkAnswerActionButtons: false,
      showNotifyMarkAnswerValidationError: false
    }
    this.saveCloseInactiveTopicsRule = this.saveCloseInactiveTopicsRule.bind(this)
    this.resetCloseInactiveTopicsSetting = this.resetCloseInactiveTopicsSetting.bind(this)
    this.onCloseInactiveTopicsSettingChanged = this.onCloseInactiveTopicsSettingChanged.bind(this)
    this.onInputChange = this.onInputChange.bind(this)
    this.onInputFocus = this.onInputFocus.bind(this)
    this.onInputBlur = this.onInputBlur.bind(this)
    this.toggleCategoryDropdown = this.toggleCategoryDropdown.bind(this)
    this.onResetCategories = this.onResetCategories.bind(this)
    this.onSelectedCategoriesChanged = this.onSelectedCategoriesChanged.bind(this)

    this.saveNotifyMarkAnswerRule = this.saveNotifyMarkAnswerRule.bind(this)
    this.onNotifyAfterInputChange = this.onNotifyAfterInputChange.bind(this)
    this.onNotifyAfterInputFocus = this.onNotifyAfterInputFocus.bind(this)
    this.onNotifyAfterInputBlur = this.onNotifyAfterInputBlur.bind(this)
    this.resetNotifyMarkAnswerSetting = this.resetNotifyMarkAnswerSetting.bind(this)
    this.onNotifyMarkAnswerSettingChanged = this.onNotifyMarkAnswerSettingChanged.bind(this)

    this.closeModal = this.closeModal.bind(this)
  }

  componentDidMount () {
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => this.onUnload(e))
  }

  componentWillUnmount () {
    window.removeEventListener('beforeunload', this.onUnload)
  }

  onUnload (e: BeforeUnloadEvent) {
    if (this.checkPanelHasUnsavedChanges()) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  async saveCloseInactiveTopicsRule () {
    const { closeTopicsIn } = this.state
    let invalidInputForCloseTopicsIn = closeTopicsIn === undefined || closeTopicsIn === null || closeTopicsIn <= 0
    if (invalidInputForCloseTopicsIn && this.state.isCloseInactiveTopicsEnabled === true) {
      this.setState({ showValidationError: true })
      return
    }
    this.setState({ messageType: null, isLoading: true })
    const successMessage = `'Automatically close inactive topics' settings have been updated successfully.`
    const errorMessage = 'Something went wrong, settings can\'t be updated. Please try again.'

    try {
      await axios.post('/settings/automationRules/closeInactiveTopicsSetting',
        {
          isCloseInactiveTopicsEnabled: this.state.isCloseInactiveTopicsEnabled,
          closeTopicsAfterDays: this.state.closeTopicsIn ?? 0,
          selectedCategories: this.state.selectedCategories
        })
      let updatedState = Object.assign({}, this.state.storedState)
      updatedState.closeTopicsAfter = this.state.closeTopicsIn
      updatedState.selectedCategoryList = this.state.selectedCategories
      updatedState.closeInactiveTopicsEnabled = this.state.isCloseInactiveTopicsEnabled
      this.setState({
        isLoading: false,
        storedState: updatedState,
        message: successMessage,
        messageType: 'success',
        showCloseInactiveTopicsActionButtons: false,
        activePanel: 1
      })
    } catch {
      this.setState({
        isLoading: false,
        messageType: 'error',
        message: errorMessage,
        showCloseInactiveTopicsActionButtons: false,
        activePanel: 1
      })
    }
  }

  resetCloseInactiveTopicsSetting () {
    const { closeInactiveTopicsEnabled, closeTopicsAfter, selectedCategoryList } = this.state.storedState
    this.setState({
      isCloseInactiveTopicsEnabled: closeInactiveTopicsEnabled,
      closeTopicsIn: closeTopicsAfter,
      selectedCategories: selectedCategoryList,
      showCloseInactiveTopicsActionButtons: false,
      showValidationError: false
    })
  }

  onCloseInactiveTopicsSettingChanged (value: boolean) {
    const { closeInactiveTopicsEnabled, closeTopicsAfter, selectedCategoryList } = this.state.storedState
    let showActionButtons = closeInactiveTopicsEnabled !== value || closeTopicsAfter !== this.state.closeTopicsIn || selectedCategoryList !== this.state.selectedCategories
    if (!value) {
      this.setState({ showValidationError: false })
    }
    this.setState({
      isCloseInactiveTopicsEnabled: value,
      showCloseInactiveTopicsActionButtons: showActionButtons,
      showCategoryDropdown: false,
      hasUnsavedChanges: showActionButtons,
      activePanel: 1
    })
  }

  onInputChange (value: number) {
    if (isNaN(value) || value <= 0) {
      this.setState({
        closeTopicsIn: undefined,
        showValidationError: true,
        hasUnsavedChanges: true,
        showCloseInactiveTopicsActionButtons: true,
        activePanel: 1
      })
      return
    }
    let showActionButtons = this.state.storedState.closeTopicsAfter !== value
    this.setState({
      closeTopicsIn: value,
      showCloseInactiveTopicsActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      showValidationError: false,
      activePanel: 1
    })
  }

  onInputFocus () {
    this.setState({
      showCloseInactiveTopicsActionButtons: true,
      showValidationError: false,
      showCategoryDropdown: false
    })
  }

  onInputBlur () {
    const showActionButtons = this.isCloseInactiveTopicRuleUpdated()
    this.setState({
      showCloseInactiveTopicsActionButtons: showActionButtons
    })
  }

  toggleCategoryDropdown (isCategoryDropdownVisible: boolean) {
    let showActionButtons = false
    if (!isCategoryDropdownVisible) {
      showActionButtons = this.isCloseInactiveTopicRuleUpdated()
    }
    this.setState({
      showCategoryDropdown: isCategoryDropdownVisible,
      showCloseInactiveTopicsActionButtons: showActionButtons
    })
  }

  onSelectedCategoriesChanged (id: string, data: Array<string>) {
    let showActionButtons = this.state.storedState.selectedCategoryList !== data

    this.setState({
      selectedCategories: data,
      showCloseInactiveTopicsActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      showCategoryDropdown: false
    })
  }

  onResetCategories () {
    const showActionButtons = this.isCloseInactiveTopicRuleUpdated()
    this.setState({
      showCategoryDropdown: false,
      showCloseInactiveTopicsActionButtons: showActionButtons
    })
  }

  isCloseInactiveTopicRuleUpdated () {
    const { closeInactiveTopicsEnabled, closeTopicsAfter, selectedCategoryList } = this.state.storedState
    return closeInactiveTopicsEnabled !== this.state.isCloseInactiveTopicsEnabled || closeTopicsAfter !== this.state.closeTopicsIn || selectedCategoryList !== this.state.selectedCategories
  }

  normalizeCategories (categories: any[]) {
    return categories
      .filter(parent => parent.categories)
      .map(parent => {
        return {
          parentId: parent.groupId,
          parentName: parent.groupName,
          items: parent.categories.map((child: { categoryName: any; categoryId: any }, idx: any) => {
            return {
              id: idx,
              name: child.categoryName,
              value: child.categoryId
            }
          })
        }
      })
  }

  onNotifyAfterInputChange (value: number) {
    if (isNaN(value) || value <= 0) {
      this.setState({
        notifyAfter: undefined,
        showNotifyMarkAnswerValidationError: true,
        hasUnsavedChanges: true,
        showNotifyMarkAnswerActionButtons: true,
        activePanel: 0
      })
      return
    }
    let showActionButtons = this.state.storedState.notifyAfter !== value
    this.setState({
      notifyAfter: value,
      showNotifyMarkAnswerActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      showNotifyMarkAnswerValidationError: false,
      activePanel: 0
    })
  }

  onNotifyAfterInputFocus () {
    this.setState({
      showNotifyMarkAnswerActionButtons: true,
      showNotifyMarkAnswerValidationError: false
    })
  }

  onNotifyAfterInputBlur () {
    const showActionButtons = this.isNotifyMarkAnswerRuleUpdated()
    this.setState({
      showNotifyMarkAnswerActionButtons: showActionButtons
    })
  }

  resetNotifyMarkAnswerSetting () {
    const { notifyMarkAnswerEnabled, notifyAfter } = this.state.storedState
    this.setState({
      isNotifyMarkAnswerEnabled: notifyMarkAnswerEnabled,
      notifyAfter: notifyAfter,
      showNotifyMarkAnswerActionButtons: false,
      showNotifyMarkAnswerValidationError: false
    })
  }

  async saveNotifyMarkAnswerRule () {
    const { notifyAfter } = this.state
    let invalidInputForNotifyAfter = notifyAfter === undefined || notifyAfter === null || notifyAfter <= 0
    if (invalidInputForNotifyAfter && this.state.isNotifyMarkAnswerEnabled === true) {
      this.setState({ showNotifyMarkAnswerValidationError: true })
      return
    }
    this.setState({ messageType: null, isLoading: true })
    const successMessage = `'Ask end users to mark best answer via email' settings have been updated successfully.`
    const errorMessage = 'Something went wrong, settings can\'t be updated. Please try again.'

    try {
      await axios.post('/settings/automationRules/notifyMarkAnswerSetting',
        {
          isNotifyMarkAnswerEnabled: this.state.isNotifyMarkAnswerEnabled,
          notifyAfter: this.state.notifyAfter ?? 0
        })
      let updatedState = Object.assign({}, this.state.storedState)
      updatedState.notifyAfter = this.state.notifyAfter
      updatedState.notifyMarkAnswerEnabled = this.state.isNotifyMarkAnswerEnabled
      this.setState({
        isLoading: false,
        storedState: updatedState,
        message: successMessage,
        messageType: 'success',
        showNotifyMarkAnswerActionButtons: false
      })
    } catch {
      this.setState({
        isLoading: false,
        messageType: 'error',
        message: errorMessage,
        showNotifyMarkAnswerActionButtons: false
      })
    }
  }

  onNotifyMarkAnswerSettingChanged (value: boolean) {
    const { notifyMarkAnswerEnabled, notifyAfter } = this.state.storedState
    let showActionButtons = notifyMarkAnswerEnabled !== value || notifyAfter !== this.state.closeTopicsIn
    if (!value) {
      this.setState({ showNotifyMarkAnswerValidationError: false })
    }
    this.setState({
      isNotifyMarkAnswerEnabled: value,
      showNotifyMarkAnswerActionButtons: showActionButtons,
      hasUnsavedChanges: showActionButtons,
      activePanel: 0
    })
  }

  isNotifyMarkAnswerRuleUpdated () {
    const { notifyMarkAnswerEnabled, notifyAfter } = this.state.storedState
    return notifyMarkAnswerEnabled !== this.state.isNotifyMarkAnswerEnabled || notifyAfter !== this.state.notifyAfter
  }

  closeModal () {
    this.setState({ showUnsavedChangesModal: false })
  }

  checkPanelHasUnsavedChanges () {
    return this.isNotifyMarkAnswerRuleUpdated() || this.isCloseInactiveTopicRuleUpdated()
  }

  resetChanges () {
    const hasCloseInactiveTopicsRuleUpdated = this.isCloseInactiveTopicRuleUpdated()
    const hasNotifyMarkAnswerRuleUpdated = this.isNotifyMarkAnswerRuleUpdated()

    if (hasNotifyMarkAnswerRuleUpdated) {
      this.setState({ activePanel: 1 })
      this.resetNotifyMarkAnswerSetting()
    }
    if (hasCloseInactiveTopicsRuleUpdated) {
      this.setState({ activePanel: 0 })
      this.resetCloseInactiveTopicsSetting()
      this.onResetCategories()
    }
    this.setState({ hasUnsavedChanges: false, showUnsavedChangesModal: false })
  }

  render ({ categories }: Props, { storedState, selectedCategories, closeTopicsIn, isCloseInactiveTopicsEnabled, showCloseInactiveTopicsActionButtons, showValidationError, message, messageType, showUnsavedChangesModal, showCategoryDropdown, isLoading, isNotifyMarkAnswerEnabled, notifyAfter, showNotifyMarkAnswerActionButtons, showNotifyMarkAnswerValidationError, activePanel } : State) {
    const isVisibleFor = messageType === 'success' ? 5000 : false
    const closeInactiveTopicsStatusText = storedState.closeInactiveTopicsEnabled ? 'on' : 'off'
    const notifyMarkAnswerStatusText = storedState.notifyMarkAnswerEnabled ? 'on' : 'off'
    const categoryDropdownId = 'categoryDropdownFilter'
    const panelHasUnsavedChanges = this.checkPanelHasUnsavedChanges()

    const CloseInactiveTopicsPanel = {
      title: <CloseInactiveTopicsPanelHeader statusText={closeInactiveTopicsStatusText} />,
      body: <CloseInactiveTopicsPanelBody
        isCloseInactiveTopicsEnabled={isCloseInactiveTopicsEnabled}
        selectedCategories={selectedCategories}
        categories={this.normalizeCategories(categories)}
        closeTopicsIn={closeTopicsIn}
        showCategoryDropdown={showCategoryDropdown}
        showActionButtons={showCloseInactiveTopicsActionButtons}
        showValidationError={showValidationError}
        onSave={() => this.saveCloseInactiveTopicsRule()}
        onCancel={() => this.resetCloseInactiveTopicsSetting()}
        onResetCategories={() => this.onResetCategories()}
        onSelectedCategoriesChanged={
          (categoryDropdownId: string, selectedCategories: Array<string>) => this.onSelectedCategoriesChanged(categoryDropdownId, selectedCategories)
        }
        categoryDropdownId={categoryDropdownId}
        onInputChange={(value: string) => this.onInputChange(parseInt(value))}
        onInputFocus={() => this.onInputFocus()}
        onInputBlur={() => this.onInputBlur()}
        toggleCategoryDropdown={() => this.toggleCategoryDropdown(!showCategoryDropdown)}
        onCloseInactiveTopicsSettingChanged={() => this.onCloseInactiveTopicsSettingChanged(!isCloseInactiveTopicsEnabled)} />,
      hasUnsavedChanges: panelHasUnsavedChanges,
      customClass: 'panel-with-dropdown-in-body'
    }
    const NotifyMarkAnswerPanel = {
      title: <NotifyToMarkAnswerPanelHeader statusText={notifyMarkAnswerStatusText} />,
      body: <NotifyToMarkAnswerPanelBody
        isNotifyMarkAnswerEnabled={isNotifyMarkAnswerEnabled}
        notifyAfter={notifyAfter}
        showActionButtons={showNotifyMarkAnswerActionButtons}
        showValidationError={showNotifyMarkAnswerValidationError}
        onSave={() => this.saveNotifyMarkAnswerRule()}
        onCancel={() => this.resetNotifyMarkAnswerSetting()}
        onInputChange={(value: string) => this.onNotifyAfterInputChange(parseInt(value))}
        onInputFocus={() => this.onNotifyAfterInputFocus()}
        onInputBlur={() => this.onNotifyAfterInputBlur()}
        onNotifyMarkAnswerSettingChanged={() => this.onNotifyMarkAnswerSettingChanged(!isNotifyMarkAnswerEnabled)} />,
      hasUnsavedChanges: panelHasUnsavedChanges
    }
    return (
      <div className='automation-rules-overview qa-automation-rules-overview'>
        <Header
          title='Automation Rules'
          disableCancelButton
          disableSaveButton
        />
        {isLoading && <Loader />}
        {
          messageType ? <AlertComponent messageType={messageType} message={message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
        }
        <AccordionPanel
          defaultActivePanelIndex={activePanel}
          panels={[NotifyMarkAnswerPanel, CloseInactiveTopicsPanel]}
          renderUnsavedChangesModal={() => this.setState({ showUnsavedChangesModal: true })}
        />
        <Modal isOpen={showUnsavedChangesModal} onClose={() => this.closeModal()}>
          <div className='unsaved-changes-modal'>
            <h3 className='modal-title'>Unsaved Changes</h3>
            <div className='modal-description'>You have unsaved changes. Do you want to discard them?</div>
            <ButtonControl size='medium' variant='brand' newStyle title='Discard changes' onClick={() => this.resetChanges()} />
            <ButtonControl newStyle size='medium' title='Cancel' onClick={() => this.closeModal()} />
          </div>
        </Modal>
      </div>
    )
  }
}
export default AutomationRules
